import React from 'react';
import { Field, reduxForm } from 'redux-form';
import { Avatar, Chip } from 'material-ui';
import RaisedButton from 'material-ui/RaisedButton';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import style from '../../../styles/styles';
import {
    renderFavoriteCheckbox,
    renderSuperSelectField,
    renderTextField,
    renderVisibleCheckBox,
} from '../../../services/form';
import ControlledEditor from '../../form/ControlledEditor/ControlledEditor';
import withTranslation from '../../../hoc/withTranslation';
import { RICHTEXT } from '../../../constants/customfiled';

const validate = values => {
    const errors = {};
    [
        'name',
        'description',
    ].forEach(field => {
        if (!values[field]) {
            errors[field] = i18n.t('generic.required');
        }
    });
    if (
        values['tag'] !== undefined
        && values['tag'] !== null
        && values['tag'] !== ''
        && values['tag'].length > 100
    ) {
        errors['tag'] = i18n.t('generic.tag_length_gt_100');
    }
    return errors;
};

export class TrailForm extends React.Component {

    static propTypes = {
        customFields: PropTypes.arrayOf(PropTypes.object),
        types: PropTypes.arrayOf(PropTypes.object),
        onFormReset: PropTypes.func,
        trail: PropTypes.object,
    };

    static defaultProps = {
        types: [],
        customFields: [],
    };

    // Handlers

    onResetForm = () => {
        this.props.reset();

        if (this.props.onFormReset) {
            this.props.onFormReset();
        }
    };

    // Render

    renderTypesSelection = (types, hintText) => {
        if (!types || types.length === 0) {
            return hintText;
        }

        return (
            <div style={style.chip.wrapper}>
                {types.map(({
                    value,
                    label,
                }, index) => {

                    let type = this.props.types.find((type) => {
                        return type.id === value;
                    });

                    if (type) {
                        return (
                            <Chip
                                key={value}
                                style={style.chip.margin}
                            >
                                <Avatar
                                    icon={type.picture && <img
                                        src={type.picture}
                                        alt=""
                                        style={style.image.contain}
                                    />}
                                    size={20}
                                    backgroundColor={type.color}
                                    style={{ overflow: 'hidden' }}
                                />
                                {type.name}
                            </Chip>
                        );
                    } else {
                        return hintText;
                    }
                })}
            </div>
        );
    };

    render() {
        // For a dropzone field, inspire from
        // https://github.com/BBB/dropzone-redux-form-example/blob/master/src/App.js
        // https://github.com/erikras/redux-form/issues/71

        const {
            handleSubmit,
            pristine,
            submitting,
        } = this.props;

        return (
            <form onSubmit={handleSubmit}>
                <div>
                    <Field
                        id="is_favorite"
                        name="is_favorite"
                        component={renderFavoriteCheckbox}
                        label="Favorite"
                    />
                    <Field
                        style={{ marginTop: '10px' }}
                        id="visible"
                        name="visible"
                        component={renderVisibleCheckBox}
                        label="Visible"
                    />
                    <Field
                        id="name"
                        name="name"
                        component={renderTextField}
                        label={this.props.t('generic.name')}
                        fullWidth={true}
                    />
                    <Field
                        id="description"
                        name="description"
                        component={ControlledEditor}
                        initialValue={(
                            this.props.initialValues && this.props.initialValues.description
                        ) || ''}
                        label="Description"
                        fullWidth={true}
                    />
                    <Field
                        id="tag"
                        name="tag"
                        component={renderTextField}
                        label="Tag"
                        fullWidth={true}
                    />
                    <Field
                        id="types_id"
                        name="types_id"
                        label="Types"
                        style={{ marginTop: 30 }}
                        component={renderSuperSelectField}
                        fullWidth={true}
                        selectionsRenderer={this.renderTypesSelection}
                        normalize={values => {
                            if (!values) {
                                return [];
                            }

                            return values.map(({
                                value,
                                label,
                            }) => {
                                return value;
                            });
                        }}
                        format={values => {
                            if (!values) {
                                return [];
                            }

                            return values.map(typeId => {
                                const type = this.props.types.find(type => type.id === typeId);
                                return {
                                    value: typeId,
                                    label: type ? type.name : typeId,
                                };
                            });
                        }}
                    >
                        {this.props.types.map((type) => (
                            <div
                                key={type.id}
                                value={type.id}
                                label={type.name}
                            >
                                <Avatar
                                    style={style.margin.right.small}
                                    icon={type.picture && <img
                                        src={type.picture}
                                        alt=""
                                        style={style.image.contain}
                                    />}
                                    size={20}
                                    backgroundColor={type.color}
                                />
                                {type.name}
                            </div>
                        ))}
                    </Field>
                    {this.props.customFields && this.props.customFields.map((cf) => {
                        let name = cf.name;

                        if (cf.tag && cf.tag.length > 0) {
                            name += ' (' + cf.tag + ')';
                        }
                        if (cf.type === RICHTEXT) {
                            return (
                                <Field
                                    key={cf.id}
                                    id={cf.id}
                                    name={cf.id}
                                    component={ControlledEditor}
                                    initialValue={this.props.initialValues
                                        ? this.props.initialValues[cf.id]
                                        : ''}
                                    label={name}
                                    fullWidth={true}
                                />
                            );
                        } else {
                            return (
                                <Field
                                    key={cf.id}
                                    id={cf.id}
                                    name={cf.id}
                                    component={renderTextField}
                                    label={name}
                                    fullWidth={true}
                                />
                            );
                        }
                    })}
                </div>
                <div style={style.formButtons}>
                    <RaisedButton
                        style={style.formButton}
                        label={this.props.t('generic.save')}
                        disabled={pristine || submitting}
                        type="submit"
                        primary={true}
                    />
                    {this.props.trail == null &&
                        <RaisedButton
                            style={style.formButton}
                            label={this.props.t('generic.save_and_create_new')}
                            disabled={pristine || submitting}
                            type="button"
                            primary={true}
                            onClick={() => {
                                // In case of success, handle submit returns undefined, otherwise it returns the list of errors
                                if (handleSubmit() === undefined) {
                                    this.onResetForm();
                                }
                            }}
                        />
                    }
                    <RaisedButton
                        style={style.formButton}
                        label={this.props.t('generic.reset')}
                        type="button"
                        disabled={pristine || submitting}
                        onClick={this.onResetForm}
                    />
                </div>
            </form>
        );
    }
}

export default reduxForm({
    form: 'editTrail',
    validate,
})(withTranslation(TrailForm));



// WEBPACK FOOTER //
// src/components/openexplore/TrailForm/TrailForm.js